import api from "Common/api";
import { ChatAttachment, ChatConversation, ChatMessage, ChatProject, ChatReaction, ChatUser } from "./types";

export async function listChatConversations(): Promise<ChatConversation[]> {
  const result: any = await api.get("/v1/api/chat/conversations");
  return result.conversations || [];
}

export async function listChatMessages(conversationId: string, before?: number) {
  const query = before ? `?before=${before}` : "";
  return api.get<any, { messages: ChatMessage[]; hasMore: boolean }>(
    `/v1/api/chat/conversations/${conversationId}/messages${query}`
  );
}

export async function sendChatMessage(
  conversationId: string,
  body: string,
  attachmentIds: string[] = [],
  messageType: "text" | "file" | "image" = "text",
  replyToMessageId?: string
): Promise<ChatMessage> {
  const result: any = await api.post(`/v1/api/chat/conversations/${conversationId}/messages`, {
    body,
    attachmentIds,
    messageType,
    replyToMessageId,
    clientMessageId: crypto.randomUUID(),
  });
  return result.message;
}

export async function setChatReaction(
  conversationId: string,
  messageId: string,
  emoji: string,
  active: boolean
): Promise<{ messageId: string; reactions: ChatReaction[] }> {
  return api.put(`/v1/api/chat/conversations/${conversationId}/messages/${messageId}/reaction`, {
    emoji,
    active,
  });
}

export async function markChatRead(conversationId: string, messageId?: string) {
  return api.post(`/v1/api/chat/conversations/${conversationId}/read`, { messageId });
}

export async function listChatContacts({
  search = "",
  type = "direct",
  projectId,
}: {
  search?: string;
  type?: "direct" | "project" | "team";
  projectId?: number;
} = {}): Promise<ChatUser[]> {
  const params = new URLSearchParams({ search, type });
  if (projectId) params.set("projectId", String(projectId));
  const result: any = await api.get(`/v1/api/chat/contacts?${params.toString()}`);
  return result.contacts || [];
}

export async function listChatProjects(): Promise<ChatProject[]> {
  const result: any = await api.get("/v1/api/chat/projects");
  return result.projects || [];
}

export async function createChatConversation(input: {
  type: ChatConversation["type"];
  title?: string;
  projectId?: number;
  participantIds: number[];
}): Promise<ChatConversation> {
  const result: any = await api.post("/v1/api/chat/conversations", input);
  return result.conversation;
}

export async function uploadChatAttachment(conversationId: string, file: File): Promise<ChatAttachment> {
  const form = new FormData();
  form.append("file", file);
  const result: any = await api.post(`/v1/api/chat/conversations/${conversationId}/attachments`, form, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return result.attachment;
}

export async function openChatAttachment(attachmentId: string) {
  const result = await getChatAttachmentUrl(attachmentId);
  window.open(result.url, "_blank", "noopener,noreferrer");
}

export async function getChatAttachmentUrl(attachmentId: string): Promise<{ url: string; expiresIn: number }> {
  return api.get(`/v1/api/chat/attachments/${attachmentId}/url`);
}

export type AdminBroadcastAudience = "homeowner" | "contractor" | "real_estate_agent" | "relibuild_app" | "tradesmens_app";

export async function listAdminBroadcastRecipients(
  audience: AdminBroadcastAudience,
  search = ""
): Promise<{ recipients: ChatUser[]; total: number; matchedTotal: number }> {
  const params = new URLSearchParams({ audience, search });
  return api.get(`/v1/api/chat/admin/broadcast-recipients?${params.toString()}`);
}

export async function sendAdminChatBroadcast(input: {
  audience: AdminBroadcastAudience;
  body: string;
  selectAll: boolean;
  recipientIds: number[];
  excludedIds: number[];
}): Promise<{ sentCount: number }> {
  return api.post("/v1/api/chat/admin/broadcast", input);
}

export async function sendAdminAppBroadcast(input: {
  audience: "relibuild" | "tradesmens";
  body: string;
}): Promise<{ sentCount: number }> {
  return api.post("/v1/api/chat/admin/app-broadcast", input);
}
